import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import DmeTag from "@/components/DmeTag";
import { Loader2, ArrowUpRight, ArrowDownRight, Calendar } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog/posts", { params: { limit: 60 } })
      .then(r => setPosts(r.data.posts || []))
      .catch(() => setPosts([]));
  }, []);

  const canonical = "https://dynastymetrics.com/blog";
  const desc = "The Dynasty Wire — a fresh daily dynasty fantasy football market report tracking proprietary DME value movers, risers, fallers and league news. Updated every day.";
  const ld = (posts && posts.length) ? {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Dynasty Metrics — Daily Market Report",
    "description": desc,
    "url": canonical,
    "blogPost": posts.slice(0, 30).map(p => ({
      "@type": "BlogPosting",
      "headline": p.title,
      "datePublished": p.date,
      "url": `https://dynastymetrics.com/blog/${p.slug}`,
      "description": p.description,
    })),
  } : null;

  return (
    <PublicShell>
      <Helmet>
        <title>The Dynasty Wire — Daily Dynasty Market Report | Dynasty Metrics</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="The Dynasty Wire — Daily Dynasty Market Report" />
        <meta property="og:url" content={canonical} />
        <link rel="alternate" type="application/rss+xml" title="Dynasty Metrics Market Report" href="https://dynastymetrics.com/api/feed.xml" />
        {ld && <script type="application/ld+json">{JSON.stringify(ld)}</script>}
      </Helmet>

      <div className="space-y-6 fade-up" data-testid="blog-index">
        <header>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// THE DYNASTY WIRE</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight" data-testid="blog-heading">Daily Dynasty Market Report</h1>
          <p className="text-[var(--text-2)] mt-2 max-w-2xl flex flex-wrap items-center gap-1">
            Every day we publish the biggest <span className="inline-flex items-center gap-1 text-[var(--accent)]">DME value <DmeTag /></span> movers across the dynasty market — risers, fallers and the news driving them.
          </p>
        </header>

        {posts === null ? (
          <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
        ) : posts.length === 0 ? (
          <div className="card-bordered p-10 text-center text-sm text-[var(--text-3)]">No reports published yet — check back tomorrow.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {posts.map(p => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="card-bordered p-5 hover:border-[var(--accent)]/40 transition-colors group" data-testid={`blog-card-${p.slug}`}>
                <div className="flex items-center gap-1.5 text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-2">
                  <Calendar className="w-3 h-3" />{p.label || p.date}
                </div>
                <div className="font-display text-lg font-bold group-hover:text-[var(--accent)] transition-colors">{p.title}</div>
                <p className="text-sm text-[var(--text-2)] mt-1.5 line-clamp-2">{p.description}</p>
                <div className="flex flex-wrap gap-3 mt-3 text-xs">
                  {(p.risers || []).slice(0, 2).map(r => (
                    <span key={r.slug} className="inline-flex items-center gap-1 text-[var(--accent)]"><ArrowUpRight className="w-3 h-3" />{r.name} +{r.change_pct}%</span>
                  ))}
                  {(p.fallers || []).slice(0, 1).map(f => (
                    <span key={f.slug} className="inline-flex items-center gap-1 text-[var(--negative)]"><ArrowDownRight className="w-3 h-3" />{f.name} {f.change_pct}%</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="card-bordered p-6 text-center" data-testid="blog-cta">
          <div className="font-display text-xl font-bold mb-2">Track your own roster's value daily</div>
          <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-4">Sync your Sleeper league for value alerts, a free Trade Calculator, and rankings re-priced to your scoring.</p>
          <Link to="/login?signup=1" className="btn-primary text-sm inline-block" data-testid="blog-cta-btn">Start free</Link>
        </div>
      </div>
    </PublicShell>
  );
}
